import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Coins, Trash2, Plus, Save, Info } from "lucide-react";
import { REGIONES } from "@/lib/constants";
import AdminNav from "@/components/AdminNav";

const CLP = (n) => new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n || 0);

const SERVICE_TYPES = [
  { code: "delegation", label: "Delegación" },
  { code: "mediation", label: "Mediación" },
];

const TASK_TYPES = {
  delegation: [
    { code: "audiencia", label: "Audiencia" },
    { code: "escrito", label: "Escrito" },
    { code: "tramite", label: "Trámite" },
    { code: "gestion", label: "Gestión" },
    { code: "otro", label: "Otro" },
  ],
  mediation: [
    { code: "mediacion", label: "Mediación (genérica)" },
    { code: "presencial", label: "Presencial" },
    { code: "remota", label: "Remota" },
  ],
};

const EMPTY_FORM = {
  service_type: "delegation",
  task_type: "",
  region: "",
  comuna: "",
  tarifa_referencial_clp: 0,
  active: true,
  notes: "",
};

export default function AdminMarketRates() {
  const [items, setItems] = useState([]);
  const [minFee, setMinFee] = useState(30000);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/market-rates");
      setItems(data.items || []);
      setMinFee(data.min_colega_fee_clp || 30000);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm(EMPTY_FORM); setEditingId(null); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      const payload = { ...form, tarifa_referencial_clp: parseInt(form.tarifa_referencial_clp || 0, 10) };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      if (editingId) await api.put(`/admin/market-rates/${editingId}`, payload);
      else await api.post("/admin/market-rates", payload);
      resetForm();
      await load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const startEdit = (r) => { setEditingId(r.id); setForm({ ...EMPTY_FORM, ...r, tarifa_referencial_clp: r.tarifa_referencial_clp }); };
  const remove = async (r) => {
    if (!window.confirm(`¿Eliminar tarifa referencial ${r.service_type} ${r.task_type || ""} ${r.comuna || r.region || "(global)"}?`)) return;
    try { await api.delete(`/admin/market-rates/${r.id}`); await load(); }
    catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="container-app py-10" data-testid="admin-market-rates">
      <AdminNav />
      <div className="label-eyebrow">Panel administrativo</div>
      <h1 className="serif text-4xl mt-2 flex items-center gap-2"><Coins size={28} className="text-amber-300"/> Tarifas referenciales de mercado</h1>
      <p className="mt-3 text-sm text-muted-foreground max-w-3xl">
        El cargo Colega Link se cobra sobre el <strong>mayor</strong> entre el honorario declarado y la tarifa referencial registrada aquí (matching por servicio + tipo + comuna/región). Sirve como piso técnico para evitar la fuga de la intermediación.
      </p>
      <div className="mt-3 p-3 rounded-md bg-white/5 border border-white/10 inline-flex items-center gap-2 text-xs">
        <Info size={14} className="text-cyan-300"/>
        <span>Cargo mínimo absoluto vigente: <strong className="text-foreground">{CLP(minFee)}</strong> por operación (configurable vía <code>MIN_COLEGA_FEE_CLP</code>).</span>
      </div>

      <form onSubmit={submit} className="mt-8 panel p-6 grid md:grid-cols-3 gap-4" data-testid="rate-form">
        <div>
          <label className="text-xs label-eyebrow">Tipo de servicio</label>
          <select className="field mt-1 w-full" value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value, task_type: "" })} data-testid="rate-service-type">
            {SERVICE_TYPES.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs label-eyebrow">Tipo de tarea (opcional)</label>
          <select className="field mt-1 w-full" value={form.task_type} onChange={(e) => setForm({ ...form, task_type: e.target.value })} data-testid="rate-task-type">
            <option value="">— Todos —</option>
            {(TASK_TYPES[form.service_type] || []).map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs label-eyebrow">Tarifa referencial (CLP)</label>
          <input type="number" min={0} step={5000} className="field mt-1 w-full" value={form.tarifa_referencial_clp}
            onChange={(e) => setForm({ ...form, tarifa_referencial_clp: e.target.value })} required data-testid="rate-tarifa"/>
        </div>
        <div>
          <label className="text-xs label-eyebrow">Región (opcional)</label>
          <select className="field mt-1 w-full" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value, comuna: "" })} data-testid="rate-region">
            <option value="">— Global —</option>
            {REGIONES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs label-eyebrow">Comuna (opcional)</label>
          <input className="field mt-1 w-full" value={form.comuna} onChange={(e) => setForm({ ...form, comuna: e.target.value })} placeholder="Ej: Santiago" data-testid="rate-comuna"/>
        </div>
        <div className="flex items-end gap-2">
          <label className="text-xs inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="rate-active"/>
            <span>Activa</span>
          </label>
        </div>
        <div className="md:col-span-3">
          <label className="text-xs label-eyebrow">Notas (opcional)</label>
          <input className="field mt-1 w-full" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Fuente, criterio, fecha..." data-testid="rate-notes"/>
        </div>
        <div className="md:col-span-3 flex gap-3">
          <button type="submit" className="btn-primary inline-flex items-center gap-2" disabled={saving} data-testid="rate-submit">
            {editingId ? <><Save size={14}/> Actualizar</> : <><Plus size={14}/> Crear tarifa</>}
          </button>
          {editingId && <button type="button" onClick={resetForm} className="btn-ghost" data-testid="rate-cancel">Cancelar</button>}
        </div>
        {error && <div className="md:col-span-3 text-sm text-[hsl(var(--destructive))]" data-testid="rate-error">{error}</div>}
      </form>

      <h2 className="serif text-2xl mt-10">Tarifas registradas ({items.length})</h2>
      {items.length === 0 ? (
        <div className="panel p-10 text-center text-muted-foreground mt-4" data-testid="rate-empty">
          Aún no hay tarifas cargadas. Mientras no existan tarifas, sólo aplica el cargo mínimo absoluto de {CLP(minFee)}.
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto panel">
          <table className="min-w-full text-sm">
            <thead className="text-xs uppercase text-muted-foreground border-b border-white/10">
              <tr>
                <th className="text-left p-3">Servicio</th>
                <th className="text-left p-3">Tarea</th>
                <th className="text-left p-3">Región / Comuna</th>
                <th className="text-right p-3">Tarifa</th>
                <th className="text-center p-3">Activa</th>
                <th className="text-right p-3">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`rate-row-${r.id}`}>
                  <td className="p-3">{SERVICE_TYPES.find((s) => s.code === r.service_type)?.label || r.service_type}</td>
                  <td className="p-3 text-muted-foreground">{r.task_type || "—"}</td>
                  <td className="p-3 text-muted-foreground">{r.comuna || r.region || "Global"}</td>
                  <td className="p-3 text-right font-medium">{CLP(r.tarifa_referencial_clp)}</td>
                  <td className="p-3 text-center">
                    {r.active
                      ? <span className="pill badge-verified text-xs">Sí</span>
                      : <span className="pill text-xs">No</span>}
                  </td>
                  <td className="p-3 text-right">
                    <button onClick={() => startEdit(r)} className="text-xs link-underline mr-3" data-testid={`rate-edit-${r.id}`}>Editar</button>
                    <button onClick={() => remove(r)} className="text-xs link-underline text-[hsl(var(--destructive))] inline-flex items-center gap-1" data-testid={`rate-delete-${r.id}`}>
                      <Trash2 size={12}/> Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
